import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import SectionCard from "@/components/customers/SectionCard";
import { useDeleteFreelancerContact } from "@/hooks/use-freelancer-contacts";
import { formatPhoneDisplay, normalizePhoneE164 } from "@/lib/phone-format";
import { contactRoleLabel } from "@/lib/freelancer-contacts";
import type { FreelancerListRow } from "@/lib/freelancers";
import type { DBFreelancerContact } from "@/lib/db-types";
import { FreelancerContactDialog } from "./FreelancerContactDialog";

function copy(text: string) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Kopiert"),
    () => toast.error("Kunne ikke kopiere"),
  );
}

function ContactLine({ icon, href, text }: { icon: "phone" | "mail"; href: string; text: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <a href={href} className="flex min-w-0 items-center gap-2 text-sm hover:underline">
        <SrIcon name={icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate">{text}</span>
      </a>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copy(text)}>Kopier</Button>
    </div>
  );
}

function Person({ name, role, phone, email, actions }: { name: string; role: string; phone: string | null; email: string | null; actions?: React.ReactNode }) {
  return (
    <li className="space-y-1.5 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={name} size="sm" className="shrink-0" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{name}</div>
            <div className="text-xs text-muted-foreground">{role}</div>
          </div>
        </div>
        {actions}
      </div>
      {phone && <ContactLine icon="phone" href={`tel:${normalizePhoneE164(phone) ?? phone}`} text={formatPhoneDisplay(phone)} />}
      {email && <ContactLine icon="mail" href={`mailto:${email}`} text={email} />}
    </li>
  );
}

/**
 * VIB-2934: kontaktpersoner. Frilanserens egne felt vises øverst («Seg selv»,
 * eller «Firmaet» for firma); under står agenter, produsenter og andre kontakter.
 */
export function FreelancerContactsSection({ freelancer: f, contacts }: { freelancer: FreelancerListRow; contacts: DBFreelancerContact[] }) {
  const remove = useDeleteFreelancerContact();
  const [editing, setEditing] = useState<DBFreelancerContact | null>(null);
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const own = f.phone || f.email;
  const total = contacts.length + (own ? 1 : 0);

  const openDialog = (c: DBFreelancerContact | null) => {
    setEditing(c);
    setOpenCount((n) => n + 1);
    setOpen(true);
  };

  return (
    <>
      <SectionCard
        title="Kontaktpersoner"
        compact
        meta={total > 0 ? String(total) : undefined}
        actions={<Button variant="ghost" size="sm" onClick={() => openDialog(null)} aria-label="Legg til kontaktperson"><SrIcon name="plus" className="h-4 w-4" />Legg til</Button>}
        empty={total === 0 ? "Ingen kontaktinfo ennå. Legg til en kontaktperson eller agent." : undefined}
      >
        {total > 0 && (
          <ul className="divide-y divide-border">
            {own && <Person name={f.name} role={f.kind === "company" ? "Firmaet" : "Seg selv"} phone={f.phone} email={f.email} />}
            {contacts.map((c) => (
              <Person
                key={c.id}
                name={c.name}
                role={contactRoleLabel(c)}
                phone={c.phone}
                email={c.email}
                actions={
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openDialog(c)} aria-label={`Rediger ${c.name}`}><SrIcon name="edit" className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => remove.mutate({ id: c.id, freelancerId: f.id })} aria-label={`Fjern ${c.name}`}><SrIcon name="trash" className="h-3.5 w-3.5" /></Button>
                  </div>
                }
              />
            ))}
          </ul>
        )}
      </SectionCard>
      <FreelancerContactDialog
        key={openCount}
        open={open}
        onOpenChange={setOpen}
        freelancerId={f.id}
        contact={editing}
        nextSortOrder={contacts.length}
      />
    </>
  );
}
